import { memo, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { Box, Icon } from 'roo-ui/components';

import { ADD_TO_CALENDAR_LINK_SELECTED_ACTION, addToCalendarLinkSelected } from 'lib/gaTracking/bookingDetailsPageEvents';
import { iconAppleSvg, iconGoogleSvg, iconWin10Svg, iconYahooSvg } from 'images';
import Dropdown from 'components/Dropdown';

import { getCalendarFields } from './lib/getCalendarFields';
import { createGoogleCalendarLink, createYahooCalendarLink, createIcs } from './lib/helpers';

const ICS_FILENAME = 'reservation.ics';
const ICS_CONTENT_TYPE = 'text/calendar;charset=utf-8';

const IconWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  margin-right: 8px;
  margin-left: -12px;
`;

const BrandIcon = ({ src, alt }) => (
  <IconWrapper>
    <img src={src} alt={alt} />
  </IconWrapper>
);

const itemToString = (item) => (item ? item.props.label : '');

const AddToCalendar = ({ booking }) => {
  const calendarFields = getCalendarFields(booking);
  const googleHref = useMemo(() => createGoogleCalendarLink(calendarFields), [calendarFields]);
  const yahooHref = useMemo(() => createYahooCalendarLink(calendarFields), [calendarFields]);
  const icsData = useMemo(() => createIcs(calendarFields), [calendarFields]);
  const icsHref = useMemo(() => encodeURI(`data:${ICS_CONTENT_TYPE},${icsData}`), [icsData]);

  const handleIcsItemSelect = useCallback(
    (item) => {
      if (window.navigator.msSaveOrOpenBlob) {
        window.navigator.msSaveOrOpenBlob(new Blob([icsData], { type: ICS_CONTENT_TYPE }), ICS_FILENAME);
      } else {
        window.open(item.props.href, '_blank');
      }
    },
    [icsData],
  );

  const handleLinkItemSelect = useCallback((item) => {
    window.open(item.props.href, '_blank');
  }, []);

  const handleDropdownSelect = useCallback((item) => {
    addToCalendarLinkSelected(ADD_TO_CALENDAR_LINK_SELECTED_ACTION[`${item.props.label.toUpperCase()}`]);
    item.props.onSelect(item);
  }, []);

  const onExpand = () => addToCalendarLinkSelected(ADD_TO_CALENDAR_LINK_SELECTED_ACTION.LINK_EXPANDED);

  return (
    <Box mt={1}>
      <Dropdown label="Add to calendar" itemToString={itemToString} onSelect={handleDropdownSelect} onExpand={onExpand}>
        <Box color="brand.primary" fontSize="sm" data-testid="add-to-calendar">
          Add to calendar <Icon name="expandMore" />
        </Box>
        <Dropdown.item label="Outlook" href={icsHref} download={ICS_FILENAME} onSelect={handleIcsItemSelect}>
          <BrandIcon src={iconWin10Svg} alt="Windows 10 Icon" /> Outlook
        </Dropdown.item>
        <Dropdown.item label="Google" href={googleHref} onSelect={handleLinkItemSelect}>
          <BrandIcon src={iconGoogleSvg} alt="Google Icon" /> Google
        </Dropdown.item>
        <Dropdown.item label="Apple" href={icsHref} download={ICS_FILENAME} onSelect={handleIcsItemSelect}>
          <BrandIcon src={iconAppleSvg} alt="Apple Icon" /> Apple
        </Dropdown.item>
        <Dropdown.item label="Yahoo" href={yahooHref} onSelect={handleLinkItemSelect}>
          <BrandIcon src={iconYahooSvg} alt="Yahoo Icon" /> Yahoo
        </Dropdown.item>
      </Dropdown>
    </Box>
  );
};

AddToCalendar.propTypes = {
  booking: PropTypes.object.isRequired,
};

export default memo(AddToCalendar);
